import React, {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
} from 'react';
import classNames from 'classnames';
import { Button, Loader, Modal } from 'ncoded-component-library';
import {
  ModalProps,
  ModalRef,
} from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import useCallbackRef from 'hooks/useCallbackRef';
import { useTranslation } from 'react-i18next';
import useGetMixpanelProps from 'hooks/useGetMixpanelProps';
import useCallbackAsync from 'hooks/useCallbackAsync';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import api from 'api';
import { SUCCESS_MESSAGES } from 'request-responses';
import { getContactNumber, Phone } from 'phoneNumberUtils';
import mixpanelService from 'services/mixpanelService';
import { MobileVerificationInitiatedEventProps } from 'models/MixpanelEvents';
import { TRANSITION_TIME, VERIFIED_PIN_ID } from 'data/global';
import { Field, Form } from 'react-final-form';
import PinField from 'router/subrouters/Dash/subrouters/private/subrouters/Profile/pages/ProfilePage/components/PinField';
import { required } from 'validations/general';

import './PhoneVerificationModal.styles.scss';

type PhoneVerificationModalProps = {
  className?: string;
  phone?: Phone;
  disabled?: boolean;
} & ModalProps;

const PhoneVerificationModal: React.ForwardRefRenderFunction<
  ModalRef,
  PhoneVerificationModalProps
> = (props, ref) => {
  const { className, disabled, phone, ...rest } = props;

  const [modal, modalRef] = useCallbackRef<ModalRef>();

  useImperativeHandle(ref, () => modal, [modal]);

  const classes = classNames('phone-verification-modal', className);

  const { t } = useTranslation();

  const getMixpanelProps = useGetMixpanelProps();

  const { popSuccess, popError } = useContext(PopNotificationsContext);
  const { currentUser, setCurrentUser } = useContext(CurrentUserContext);

  const contactNumber = useMemo(
    () => getContactNumber(phone || currentUser?.phone),
    [currentUser?.phone, phone],
  );

  const [handleSubmit, loading] = useCallbackAsync(
    async ({ verificationCode }: { verificationCode: string }) => {
      const { data } = await api.users.checkPhoneVerification(verificationCode);
      popSuccess(t(SUCCESS_MESSAGES.PHONE_NUMBER_VERIFIED));
      setCurrentUser((prev) => ({ ...prev, ...data }));
      modal?.close();
    },
  );

  const [initVerificationCode, loadingResend] = useCallbackAsync(async () => {
    if (disabled) {
      popError(t('pleaseSetYourPhoneNumber'));
      return;
    }

    await api.users.phoneVerification(contactNumber);
    popSuccess(t(SUCCESS_MESSAGES.CHECK_PHONE_INBOX));
  });

  useEffect(() => {
    if (modal?.openState && !disabled) {
      mixpanelService.mobileVerificationInitiated(
        getMixpanelProps({
          phoneNumber: contactNumber,
        }) as MobileVerificationInitiatedEventProps,
      );

      initVerificationCode();
    }
  }, [
    contactNumber,
    disabled,
    modal?.openState,
    getMixpanelProps,
    initVerificationCode,
  ]);

  useEffect(() => {
    if (modal?.openState) {
      setTimeout(
        () => document.getElementById(VERIFIED_PIN_ID)?.focus(),
        TRANSITION_TIME,
      );
    }
  }, [modal?.openState]);

  return (
    <Modal
      ref={modalRef}
      className={classes}
      title={t('verifyYourPhoneNumber')}
      {...rest}
    >
      {(loadingResend || loading) && <Loader />}

      <Form onSubmit={handleSubmit}>
        {({ handleSubmit }) => (
          <form onSubmit={handleSubmit}>
            <p>{t('weHaveSentVerificationCode')}</p>

            <Field
              name="verificationCode"
              numOfPins={4}
              component={PinField}
              validate={required(t('youMustEnterVerificationCode'))}
            />

            <div className="resend">
              {`${t('didNotGetVerificationCode')} `}
              <Button variant="link" onClick={initVerificationCode}>
                {t('resendIt')}
              </Button>
            </div>

            <div className="actions">
              <Button type="button" variant="link" onClick={modal?.close}>
                {t('cancel')}
              </Button>
              <Button disabled={disabled} type="submit">
                {t('submit')}
              </Button>
            </div>
          </form>
        )}
      </Form>
    </Modal>
  );
};

export default forwardRef(PhoneVerificationModal);
